import { useEffect, useMemo, useCallback } from 'react'
import { toast } from 'sonner'
import { useGenerationStore } from '@/stores/generationStore'
import type { Note, Asset, NoteTemplate, NoteImageSlot } from '@/types'

interface TaskRecoveryOptions {
  notes: Note[]
  templateId: string
  templateById: Record<string, NoteTemplate>
  slotMap: Record<string, NoteImageSlot[]>
  analyzedAssets: Asset[]
  runBulkImages: (
    selectedNotes: Note[],
    templateId: string,
    templateById: Record<string, NoteTemplate>,
    slotMap: Record<string, NoteImageSlot[]>,
    analyzedAssets: Asset[],
    options?: { isResume?: boolean }
  ) => Promise<{ success: number; fail: number }>
}

/**
 * 任务恢复 Hook
 * 监控中断的生成任务并提供恢复功能
 */
export function useTaskRecovery({
  notes,
  templateId,
  templateById,
  slotMap,
  analyzedAssets,
  runBulkImages,
}: TaskRecoveryOptions) {
  const activeBatchIds = useGenerationStore((state) => state.activeBatchIds)
  const clearActiveBatchIds = useGenerationStore((state) => state.clearActiveBatchIds)

  // 找出中断的笔记
  const interruptedNotes = useMemo(() => {
    if (!activeBatchIds || activeBatchIds.length === 0) return []
    
    return notes.filter((n) => 
      activeBatchIds.includes(n.id) && 
      (n.status === 'generating' || n.status === 'planning')
    )
  }, [notes, activeBatchIds])

  // 恢复任务
  const resumeTasks = useCallback(async () => {
    if (interruptedNotes.length === 0) return

    toast.info(`正在恢复 ${interruptedNotes.length} 个中断的任务...`)
    
    try {
      await runBulkImages(
        interruptedNotes,
        templateId,
        templateById,
        slotMap,
        analyzedAssets,
        { isResume: true }
      )
    } catch (err) {
      console.error('[任务恢复失败]', err)
      toast.error('任务恢复失败，请手动重试')
    }
  }, [interruptedNotes, templateId, templateById, slotMap, analyzedAssets, runBulkImages])

  // 如果检测到中断任务且当前没有正在运行的任务，弹出提示
  useEffect(() => {
    if (interruptedNotes.length > 0) {
      // 这里可以根据需求选择是否自动恢复，或者仅弹出提示
      // 目前策略：弹出 Toast 并提供恢复按钮
      toast.info(`检测到 ${interruptedNotes.length} 个中断的任务`, {
        action: {
          label: '立即恢复',
          onClick: () => resumeTasks(),
        },
        duration: 10000,
      })
    } else if (activeBatchIds.length > 0) {
      // 如果 activeBatchIds 不为空，但没有匹配的 generating/planning 笔记，说明都完成了
      // 可以在这里清理
      const allCompleted = activeBatchIds.every(id => {
        const note = notes.find(n => n.id === id)
        return note && (note.status === 'completed' || note.status === 'draft')
      })
      
      if (allCompleted) {
        clearActiveBatchIds()
      }
    }
  }, [interruptedNotes.length, resumeTasks, activeBatchIds, notes, clearActiveBatchIds])

  return {
    interruptedCount: interruptedNotes.length,
    resumeTasks,
  }
}




